// src/features/developers/tabs.ts
//
// The tab lives in the query string of the hash route (`#/developers?tab=reference`): a link in an
// e-mail to a partner opens the right tab and a reload keeps it.
import type { DeveloperTab } from './types';

export const DEVELOPER_TABS: DeveloperTab[] = ['overview', 'reference', 'recipes', 'downloads'];

/**
 * The tab a query string asks for; an unknown or missing one is the first. Read up to the first
 * slash: the interactive reference appends ITS OWN route after ours (`?tab=reference/tag/…`) when
 * the reader moves through it, and a reload with that address must still land on the reference.
 */
export const tabOfSearch = (search: string): DeveloperTab => {
  const asked = (new URLSearchParams(search).get('tab') ?? '').split('/')[0];
  return (DEVELOPER_TABS as string[]).includes(asked) ? (asked as DeveloperTab) : 'overview';
};

/** The query string for a tab, keeping whatever else the address carried. */
export const searchOfTab = (tab: DeveloperTab, search: string): string => {
  const params = new URLSearchParams(search);
  if (tab === 'overview') params.delete('tab');
  else params.set('tab', tab);
  const rest = params.toString();
  return rest ? `?${rest}` : '';
};

/**
 * Before signing in, the `tab` of a Developers address is cut at the first slash: the reference's
 * own route can carry braces (`{propertyId}`), which have no place in an address handed to the
 * sign-in redirect. Returns the cleaned hash, or `null` when there is nothing to clean.
 */
export const cleanDevelopersHash = (hash: string): string | null => {
  if (!hash.replace(/^#\/?/, '').startsWith('developers')) return null;
  const [route, query = ''] = hash.split('?');
  const params = new URLSearchParams(query);
  const tab = params.get('tab');
  if (!tab || !tab.includes('/')) return null;
  params.set('tab', tab.split('/')[0]);
  return `${route}?${params.toString()}`;
};
